/**
 * Colunas da grade com as chaves do sim-relatorios (COLUMN_DEFS de public/agenda.js): a preferência
 * fica em AGENDA_PREF_COLUNAS_SIM, a mesma linha que o original lê e grava, como [{key, visible}]
 * na ordem de exibição.
 */

import { AgendaColunaPref } from '../../../../interfaces/agenda';

export type { AgendaColunaPref };

export interface AgendaColunaDef {
  key: string;
  /** Nome no seletor. */
  rotulo: string;
  cabecalho: string;
  /** Chave aceita pelo sort do back; sem ela o cabeçalho não ordena. */
  ordenacao?: string;
  sempreVisivel?: boolean;
}

/** Ordem padrão do original, inclusive as chaves que a Fila não desenha. */
export const CHAVES_ORIGINAL: readonly string[] = [
  'checkbox', 'status_icon', 'senha', 'sequencia', 'dt_agenda', 'hora', 'tipo', 'duracao', 'espera',
  'status_agenda', 'classif', 'encaixe', 'retorno', 'transferido', 'paciente', 'dt_nascimento', 'idade',
  'telefone', 'email', 'convenio', 'categoria', 'atendimento', 'especialidade', 'agenda', 'medico', 'exame',
  'dt_agendamento', 'dt_confirmacao', 'dt_aguardando', 'dt_consulta', 'dt_atendido', 'dt_fim_atend',
  'dt_cancelamento', 'usu_origem', 'usu_confirm', 'usu_aguardando', 'usu_consulta', 'usu_atendido',
  'usu_fim_atend', 'usu_cancelamento', 'observacao', 'motivo_cancelamento',
];

/** Colunas que só a Fila tem, com a chave do original depois da qual entram. */
const CHAVES_SO_FILA: readonly [string, string][] = [['dependente', 'convenio']];

/** Ordem da Fila: a do original com as colunas próprias encaixadas. */
const ORDEM_FILA: readonly string[] = CHAVES_SO_FILA.reduce((ordem, [key, depois]) => {
  const i = ordem.indexOf(depois);
  return [...ordem.slice(0, i + 1), key, ...ordem.slice(i + 1)];
}, [...CHAVES_ORIGINAL]);

// "tipo" é imposto pelo Integrado e o original nunca a grava; "checkbox" fica fixa na Fila.
const FORA_DO_SELETOR = new Set(['checkbox', 'tipo']);

/** Colunas que a linha da grade da Fila preenche. status_icon não existe aqui e segue intocada. */
export const COLUNAS_FILA: readonly AgendaColunaDef[] = [
  { key: 'senha', rotulo: 'Senha', cabecalho: 'Senha', ordenacao: 'dsSenha' },
  { key: 'sequencia', rotulo: 'Sequência', cabecalho: 'Seq.', ordenacao: 'nrSequencia' },
  { key: 'dt_agenda', rotulo: 'Dt. Agenda', cabecalho: 'Dt. Agenda', ordenacao: 'dtAgenda' },
  { key: 'hora', rotulo: 'Hora', cabecalho: 'Hora', ordenacao: 'hrInicio', sempreVisivel: true },
  { key: 'duracao', rotulo: 'Duração', cabecalho: 'Dur.', ordenacao: 'nrMinutoDuracao' },
  { key: 'espera', rotulo: 'Espera', cabecalho: 'Espera', ordenacao: 'qtMinEspera' },
  { key: 'status_agenda', rotulo: 'Status', cabecalho: 'Status', ordenacao: 'dsStatusAgenda' },
  { key: 'classif', rotulo: 'Classificação', cabecalho: 'Classif.', ordenacao: 'dsClassifAgenda' },
  { key: 'encaixe', rotulo: 'Encaixe', cabecalho: 'Enc.', ordenacao: 'ieEncaixe' },
  { key: 'retorno', rotulo: 'Retorno', cabecalho: 'Ret.', ordenacao: 'ieRetorno' },
  { key: 'transferido', rotulo: 'Transferido', cabecalho: 'Transf.', ordenacao: 'nmUsuarioCopiaTrans' },
  { key: 'paciente', rotulo: 'Paciente', cabecalho: 'Paciente', ordenacao: 'paciente', sempreVisivel: true },
  { key: 'dt_nascimento', rotulo: 'Dt. Nascimento', cabecalho: 'Dt. Nasc.', ordenacao: 'dtNascimento' },
  { key: 'idade', rotulo: 'Idade', cabecalho: 'Idade', ordenacao: 'idade' },
  { key: 'telefone', rotulo: 'Telefone', cabecalho: 'Telefone', ordenacao: 'nrTelefone' },
  { key: 'email', rotulo: 'E-mail', cabecalho: 'E-mail', ordenacao: 'dsEmail' },
  { key: 'convenio', rotulo: 'Convênio', cabecalho: 'Convênio', ordenacao: 'convenio' },
  { key: 'dependente', rotulo: 'Dependente', cabecalho: 'Dependente' },
  { key: 'categoria', rotulo: 'Categoria', cabecalho: 'Categoria' },
  { key: 'atendimento', rotulo: 'Atendimento', cabecalho: 'Atend.' },
  { key: 'especialidade', rotulo: 'Especialidade', cabecalho: 'Especialidade', ordenacao: 'especialidade' },
  { key: 'agenda', rotulo: 'Agenda', cabecalho: 'Agenda', ordenacao: 'dsAgenda' },
  { key: 'medico', rotulo: 'Médico', cabecalho: 'Médico', ordenacao: 'medico' },
  { key: 'exame', rotulo: 'Exame', cabecalho: 'Exame', ordenacao: 'dsExame' },
  { key: 'dt_agendamento', rotulo: 'Dt. Agendamento', cabecalho: 'Dt. Agendamento', ordenacao: 'dtAgendamento' },
  { key: 'dt_confirmacao', rotulo: 'Dt. Confirmação', cabecalho: 'Dt. Confirmação', ordenacao: 'dtConfirmacao' },
  { key: 'dt_aguardando', rotulo: 'Dt. Aguardando', cabecalho: 'Dt. Aguardando', ordenacao: 'dtAguardando' },
  { key: 'dt_consulta', rotulo: 'Dt. Consulta/Exame', cabecalho: 'Dt. Cons./Exame', ordenacao: 'dtConsulta' },
  { key: 'dt_atendido', rotulo: 'Dt. Atendido', cabecalho: 'Dt. Atendido', ordenacao: 'dtAtendido' },
  { key: 'dt_fim_atend', rotulo: 'Dt. Fim Atend.', cabecalho: 'Dt. Fim Atend.', ordenacao: 'dtFimAtendimento' },
  { key: 'dt_cancelamento', rotulo: 'Dt. Cancelamento', cabecalho: 'Dt. Cancelamento', ordenacao: 'dtCancelamento' },
  { key: 'usu_origem', rotulo: 'Usu. Origem', cabecalho: 'Agendado por', ordenacao: 'nmUsuarioOrig' },
  { key: 'usu_confirm', rotulo: 'Usu. Confirmação', cabecalho: 'Usu. Confirm.', ordenacao: 'nmUsuarioConfirm' },
  { key: 'usu_aguardando', rotulo: 'Usu. Aguardando', cabecalho: 'Usu. Aguard.', ordenacao: 'nmUsuarioAguardando' },
  { key: 'usu_consulta', rotulo: 'Usu. Consulta', cabecalho: 'Usu. Consulta', ordenacao: 'nmUsuarioConsulta' },
  { key: 'usu_atendido', rotulo: 'Usu. Atendido', cabecalho: 'Usu. Atendido', ordenacao: 'nmUsuarioAtendido' },
  { key: 'usu_fim_atend', rotulo: 'Usu. Fim Atend.', cabecalho: 'Usu. Fim Atend.', ordenacao: 'usuarioFimAtend' },
  { key: 'usu_cancelamento', rotulo: 'Usu. Cancelamento', cabecalho: 'Usu. Cancel.', ordenacao: 'nmUsuarioCancelamento' },
  { key: 'observacao', rotulo: 'Observação', cabecalho: 'Obs.', ordenacao: 'dsObservacao' },
  { key: 'motivo_cancelamento', rotulo: 'Motivo do cancelamento', cabecalho: 'Motivo Cancel.', ordenacao: 'cdMotivoCancelamento' },
];

/** Sem sort, como no original: ordenar por tipo partiria o dia do médico em dois blocos. */
export const COLUNA_TIPO: AgendaColunaDef = { key: 'tipo', rotulo: 'Tipo', cabecalho: 'Tipo' };

const DEF_POR_CHAVE = new Map(COLUNAS_FILA.map(d => [d.key, d]));

export function defColuna(key: string): AgendaColunaDef | undefined {
  return DEF_POR_CHAVE.get(key);
}

/** As colunas que a grade da Fila mostrava antes do seletor, mais a Dependente, nessa ordem. */
export const VISIVEIS_PADRAO_FILA: readonly string[] = [
  'hora', 'duracao', 'status_agenda', 'paciente', 'classif', 'convenio', 'dependente', 'especialidade', 'exame',
  'medico', 'usu_origem', 'espera', 'observacao',
];

/**
 * Padrão de quem nunca salvou (e do "restaurar"): a grade enxuta de antes, e não o "tudo visível"
 * do original. As demais chaves vão ocultas no fim, na ordem do original.
 */
export function colunasPadrao(): AgendaColunaPref[] {
  const visiveis = new Set(VISIVEIS_PADRAO_FILA);
  return [
    { key: 'checkbox', visible: true },
    { key: 'status_icon', visible: true },
    ...VISIVEIS_PADRAO_FILA.map(key => ({ key, visible: true })),
    ...CHAVES_ORIGINAL
      .filter(k => k !== 'tipo' && k !== 'checkbox' && k !== 'status_icon' && !visiveis.has(k))
      .map(key => ({ key, visible: false })),
  ];
}

/**
 * O que o GET devolveu, já no formato de array. Aceita o formato antigo do original
 * ({senha: true, ...}); null = sem preferência válida.
 */
export function normalizarColunas(salvo: unknown): AgendaColunaPref[] | null {
  if (Array.isArray(salvo)) {
    const vistas = new Set<string>();
    const lista: AgendaColunaPref[] = [];
    for (const item of salvo) {
      if (!item || typeof item !== 'object') continue;
      const key = (item as { key?: unknown }).key;
      if (typeof key !== 'string' || !key || vistas.has(key)) continue;
      vistas.add(key);
      // Campos que outro app grave no item (ex.: largura) voltam intactos no próximo PUT.
      lista.push({ ...(item as object), key, visible: (item as { visible?: unknown }).visible !== false });
    }
    return lista.length ? lista : null;
  }
  if (salvo && typeof salvo === 'object') {
    const antigo = salvo as Record<string, unknown>;
    const conhecidas = CHAVES_ORIGINAL.filter(k => k !== 'tipo');
    const extras = Object.keys(antigo).filter(k => k && k !== 'tipo' && !conhecidas.includes(k));
    return [...conhecidas, ...extras].map(key => ({ key, visible: antigo[key] !== false }));
  }
  return null;
}

/**
 * Salvo + chaves do original (e as só da Fila) que faltam, visíveis, cada uma logo depois da última
 * que a precede na ordem da Fila (mesma regra do original). Chave que a Fila não conhece fica onde
 * estava. Sem nada salvo, o padrão da Fila.
 */
export function mesclarColunas(salvo: unknown): AgendaColunaPref[] {
  const base = normalizarColunas(salvo);
  if (!base) return colunasPadrao();
  const lista = base.map(p => ({ ...p }));
  ORDEM_FILA.forEach((key, i) => {
    if (key === 'tipo' || lista.some(p => p.key === key)) return;
    let pos = lista.length;
    for (let j = i - 1; j >= 0; j--) {
      const anterior = lista.findIndex(p => p.key === ORDEM_FILA[j]);
      if (anterior !== -1) { pos = anterior + 1; break; }
    }
    lista.splice(pos, 0, { key, visible: true });
  });
  return lista;
}

/** Itens do seletor (colunas que a Fila desenha), na ordem salva. */
export function itensSeletor(prefs: AgendaColunaPref[]): { def: AgendaColunaDef; visivel: boolean }[] {
  return prefs
    .filter(p => !FORA_DO_SELETOR.has(p.key) && DEF_POR_CHAVE.has(p.key))
    .map(p => ({ def: DEF_POR_CHAVE.get(p.key)!, visivel: p.visible || !!DEF_POR_CHAVE.get(p.key)!.sempreVisivel }));
}

/** Colunas desenhadas entre o checkbox e o ⋮; no Integrado a Tipo entra logo depois da Hora. */
export function colunasVisiveis(prefs: AgendaColunaPref[], integrado: boolean): AgendaColunaDef[] {
  const lista: AgendaColunaDef[] = [];
  for (const { def, visivel } of itensSeletor(prefs)) {
    if (!visivel) continue;
    lista.push(def);
    if (integrado && def.key === 'hora') lista.push(COLUNA_TIPO);
  }
  return lista;
}

export function alternarColuna(prefs: AgendaColunaPref[], key: string): AgendaColunaPref[] {
  if (defColuna(key)?.sempreVisivel) return prefs;
  return prefs.map(p => (p.key === key ? { ...p, visible: !p.visible } : p));
}

/** Escolha explícita no seletor: ligar marca "fixo" (vence o ocultar automático); desligar limpa. */
export function definirMedico(prefs: AgendaColunaPref[], visivel: boolean): AgendaColunaPref[] {
  return prefs.map(p => {
    if (p.key !== 'medico') return p;
    const { fixo: _, ...resto } = p;
    return visivel ? { ...resto, visible: true, fixo: true } : { ...resto, visible: false };
  });
}

/**
 * Reordena só entre as colunas do seletor: as posições delas no array são reaproveitadas, então
 * chaves de outro app não andam.
 */
function reordenar(prefs: AgendaColunaPref[], ordem: (chaves: string[]) => string[]): AgendaColunaPref[] {
  const posicoes: number[] = [];
  prefs.forEach((p, i) => { if (!FORA_DO_SELETOR.has(p.key) && DEF_POR_CHAVE.has(p.key)) posicoes.push(i); });
  const nova = ordem(posicoes.map(i => prefs[i].key));
  const porChave = new Map(prefs.map(p => [p.key, p]));
  const lista = [...prefs];
  posicoes.forEach((pos, i) => { lista[pos] = porChave.get(nova[i])!; });
  return lista;
}

export function moverColuna(prefs: AgendaColunaPref[], key: string, delta: -1 | 1): AgendaColunaPref[] {
  return reordenar(prefs, chaves => {
    const i = chaves.indexOf(key);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= chaves.length) return chaves;
    const nova = [...chaves];
    [nova[i], nova[j]] = [nova[j], nova[i]];
    return nova;
  });
}

/** Arrastar: a coluna passa a ocupar o lugar do alvo. */
export function moverColunaPara(prefs: AgendaColunaPref[], key: string, alvo: string): AgendaColunaPref[] {
  if (key === alvo) return prefs;
  return reordenar(prefs, chaves => {
    const i = chaves.indexOf(key);
    const j = chaves.indexOf(alvo);
    if (i < 0 || j < 0) return chaves;
    const nova = [...chaves];
    nova.splice(i, 1);
    nova.splice(j, 0, key);
    return nova;
  });
}

/** Volta ao padrão da Fila; chaves fora do dicionário seguem no fim, como estavam. */
export function restaurarColunas(prefs: AgendaColunaPref[]): AgendaColunaPref[] {
  const padrao = colunasPadrao();
  const conhecidas = new Set(padrao.map(p => p.key));
  const atuais = new Map(prefs.map(p => [p.key, p]));
  return [
    ...padrao.map(p => {
      const atual = atuais.get(p.key);
      if (!atual) return p;
      const { fixo: _, ...resto } = atual;
      return { ...resto, visible: p.visible };
    }),
    ...prefs.filter(p => !conhecidas.has(p.key)),
  ];
}
